/**
 * NetWorthCard — always-visible summary showing net worth + monthly stats.
 */
import { StatCell, Money } from "./primitives";
import { useFinancialPlan } from "@/context/FinancialPlanContext";
import { TrendingUp, TrendingDown } from "lucide-react";

export default function NetWorthCard() {
  const { plan, summary } = useFinancialPlan();
  if (!plan || !summary) return null;
  const ccy = plan.currency;
  const positive = (summary.net_worth || 0) >= 0;
  return (
    <section className="bg-card border border-border rounded-2xl p-6 mb-6" data-testid="net-worth-card">
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="text-xs font-mono uppercase tracking-widest text-muted-foreground">Net worth</div>
          <div className="flex items-baseline gap-3 mt-1">
            <div className={`font-display text-4xl font-black tabular-nums ${positive ? "" : "text-destructive"}`} data-testid="net-worth-value">
              <Money amount={summary.net_worth} currency={ccy} />
            </div>
            {positive ? <TrendingUp size={20} className="text-emerald-500" /> : <TrendingDown size={20} className="text-destructive" />}
          </div>
        </div>
        <div className="text-right text-xs text-muted-foreground">
          <div><Money amount={summary.total_assets} currency={ccy} className="text-emerald-500 font-semibold" /> assets</div>
          <div className="mt-1">− <Money amount={summary.total_liabilities} currency={ccy} className="text-destructive font-semibold" /> liabilities</div>
        </div>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        <StatCell label="Monthly income" value={<Money amount={summary.total_income_monthly} currency={ccy} />} sub="gross" testId="stat-income" />
        <StatCell label="Monthly expenses" value={<Money amount={summary.total_expenses_monthly} currency={ccy} />} sub={`${summary.savings_rate_pct}% saved`} testId="stat-expenses" tone={summary.savings_rate_pct < 10 && summary.total_income_monthly > 0 ? "warn" : "default"} />
        <StatCell label="Total debt" value={<Money amount={summary.total_debt} currency={ccy} />} sub={`avg ${summary.weighted_avg_interest_rate_pct}% APR`} testId="stat-debt" tone={summary.weighted_avg_interest_rate_pct >= 15 ? "bad" : "default"} />
        <StatCell label="DTI ratio" value={`${summary.debt_to_income_pct}%`} sub={summary.debt_to_income_pct <= 36 ? "healthy" : "high"} testId="stat-dti" tone={summary.debt_to_income_pct > 43 ? "bad" : summary.debt_to_income_pct > 36 ? "warn" : "good"} />
      </div>
    </section>
  );
}
